import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { shopTaskService } from '../../services/shopTaskService';
import { taskStatusChip } from '../../pages/ShopTasks';

export default function OrderShopTasksCard({ orderId, workFunctions = [], estimatedFunctionIds = [] }) {
  const [tasks, setTasks] = useState([]);
  const [functionIds, setFunctionIds] = useState([]);
  const [touched, setTouched] = useState(false);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');

  const estimatedKey = estimatedFunctionIds.join(',');
  useEffect(() => {
    if (!touched) setFunctionIds(estimatedKey ? estimatedKey.split(',').map(Number) : []);
  }, [estimatedKey, touched]);

  const load = useCallback(async () => {
    if (!orderId) return;
    try {
      const res = await shopTaskService.getAll({ work_order_id: orderId, limit: 200 });
      setTasks(res.data || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load tasks');
    }
  }, [orderId]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleFunction = (id) => {
    setTouched(true);
    setFunctionIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleAdd = async () => {
    if (!functionIds.length) return;
    setAdding(true);
    setError('');
    try {
      await shopTaskService.create({ work_order_id: orderId, work_function_ids: functionIds });
      setTouched(false);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to add task');
    } finally {
      setAdding(false);
    }
  };

  const totalHours = tasks.reduce((s, t) => s + Number(t.hours_logged || 0), 0);

  return (
    <Card>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Shop Tasks {tasks.length > 0 ? `· ${totalHours.toFixed(2)} h logged` : ''}
          </Typography>
          <Link component={RouterLink} to="/shop-tasks" variant="caption">
            All tasks
          </Link>
        </Stack>
        {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>{error}</Alert>}
        {tasks.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            No tasks for this order yet. Add one so employees can clock into it at the kiosk.
          </Typography>
        ) : (
          <Table size="small" sx={{ mb: 1.5 }}>
            <TableHead>
              <TableRow>
                <TableCell>Task</TableCell>
                <TableCell align="right">Hours</TableCell>
                <TableCell align="right">People</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tasks.map((t) => (
                <TableRow key={t.id}>
                  <TableCell>
                    <Link component={RouterLink} to={`/shop-tasks/${t.id}`}>{t.label}</Link>
                  </TableCell>
                  <TableCell align="right">{Number(t.hours_logged || 0).toFixed(2)}</TableCell>
                  <TableCell align="right">{t.employee_count || 0}</TableCell>
                  <TableCell>{taskStatusChip(t)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Functions for a new task (employees pick one at the kiosk)
        </Typography>
        <FormGroup row sx={{ mb: 1 }}>
          {workFunctions.map((wf) => (
            <FormControlLabel
              key={wf.id}
              control={
                <Checkbox size="small" checked={functionIds.includes(wf.id)} onChange={() => toggleFunction(wf.id)} />
              }
              label={wf.label}
            />
          ))}
        </FormGroup>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Button
            startIcon={<AddIcon />}
            size="small"
            variant="outlined"
            disabled={!functionIds.length || adding}
            onClick={handleAdd}
          >
            Add Task
          </Button>
          <Button size="small" component={RouterLink} to={`/shop-tasks/new?work_order_id=${orderId}`}>
            New task with notes…
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}
